/** Yield so the browser can paint / handle clicks during heavy route work. */
export function yieldToMain(): Promise<void> {
  const sched = (
    globalThis as typeof globalThis & {
      scheduler?: { yield?: () => Promise<void> };
    }
  ).scheduler;
  if (sched && typeof sched.yield === 'function') {
    return sched.yield();
  }
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

/** Defer non-urgent work until the browser is idle (or a short timeout). */
export function scheduleIdle(task: () => void, timeoutMs = 2_000): void {
  if (typeof window === 'undefined') {
    task();
    return;
  }
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => task(), { timeout: timeoutMs });
    return;
  }
  window.setTimeout(task, 0);
}

export async function mapInChunks<T, R>(
  items: T[],
  mapFn: (item: T, index: number) => R,
  chunkSize = 2_000,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  for (let i = 0; i < items.length; i++) {
    out[i] = mapFn(items[i]!, i);
    if (i > 0 && i % chunkSize === 0) {
      await yieldToMain();
    }
  }
  return out;
}

/** Process array items in batches, yielding between batches. */
export async function forEachChunk<T>(
  items: T[],
  chunkSize: number,
  visit: (item: T, index: number) => void,
): Promise<void> {
  for (let i = 0; i < items.length; i++) {
    visit(items[i]!, i);
    if (i > 0 && i % chunkSize === 0) {
      await yieldToMain();
    }
  }
}
